import { useState } from 'react';
import { Modal, Input, Checkbox, Textarea, useToast } from '@/shared/components/ui';
import { Save, X } from 'lucide-react';
import { tipoUsuarioService } from '../api/tipoUsuarioService';
import { CrearTipoUsuarioDto } from '@/core/sdk/modules/administracion/tipoUsuarioClient';
import { JsonFieldBuilder } from './JsonFieldBuilder';
import { LIMITES_VALIDACION, MENSAJES_ERROR } from '../constants';
import styles from './TipoUsuarioModals.module.css';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateTipoUsuarioModal = ({ isOpen, onClose, onSuccess }: Props) => {
    const [formData, setFormData] = useState<CrearTipoUsuarioDto>({
        nombre: '',
        descripcion: '',
        activo: true,
        camposJson: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const { showToast } = useToast();

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.nombre.trim()) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_REQUERIDO;
        } else if (formData.nombre.length > LIMITES_VALIDACION.NOMBRE_MAX) {
            newErrors.nombre = MENSAJES_ERROR.NOMBRE_EXCEDE_LIMITE;
        }

        if (formData.descripcion && formData.descripcion.length > LIMITES_VALIDACION.DESCRIPCION_MAX) {
            newErrors.descripcion = MENSAJES_ERROR.DESCRIPCION_EXCEDE_LIMITE;
        }

        // Validar JSON si se proporciona
        if (formData.camposJson && formData.camposJson.trim()) {
            try {
                JSON.parse(formData.camposJson);
            } catch {
                newErrors.camposJson = MENSAJES_ERROR.JSON_INVALIDO;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Limpiar camposJson si está vacío
            const dataToSend = {
                ...formData,
                camposJson: formData.camposJson?.trim() || undefined,
                descripcion: formData.descripcion?.trim() || undefined
            };

            await tipoUsuarioService.create(dataToSend);

            showToast({
                type: 'success',
                message: MENSAJES_ERROR.CREADO_EXITOSO
            });

            onSuccess();
        } catch (error: any) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                MENSAJES_ERROR.ERROR_CREAR;

            showToast({
                type: 'error',
                message: errorMessage
            });

            console.error('[CreateTipoUsuario] Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof CrearTipoUsuarioDto, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Limpiar error del campo al escribir
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Nuevo Tipo de Usuario"
            size="lg"
            closeOnOverlayClick={false}
            headerActions={
                <>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.saveButton}`}
                        onClick={handleSubmit}
                        disabled={loading}
                        title="Guardar"
                        aria-label="Guardar nuevo tipo de usuario"
                    >
                        <Save size={18} strokeWidth={2.5} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.cancelButton}`}
                        onClick={onClose}
                        disabled={loading}
                        title="Cancelar"
                        aria-label="Cancelar creación"
                    >
                        <X size={18} strokeWidth={2.5} />
                    </button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formRow}>
                    <div className={styles.formRowFlex}>
                        <Input
                            label="Nombre *"
                            value={formData.nombre}
                            onChange={(e) => handleChange('nombre', e.target.value)}
                            error={errors.nombre}
                            placeholder="Ej: Cliente, Proveedor, Empleado"
                            disabled={loading}
                        />
                    </div>
                    <div className={styles.formRowCheckbox}>
                        <Checkbox
                            label="Activo"
                            checked={formData.activo}
                            onChange={(checked) => handleChange('activo', checked)}
                            disabled={loading}
                        />
                    </div>
                </div>

                <Textarea
                    label="Descripción"
                    value={formData.descripcion || ''}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    error={errors.descripcion}
                    placeholder="Descripción opcional del tipo de usuario"
                    fullWidth
                    disabled={loading}
                    resize="vertical"
                />

                <JsonFieldBuilder
                    value={formData.camposJson || ''}
                    onChange={(json) => handleChange('camposJson', json)}
                    error={errors.camposJson}
                />
            </form>
        </Modal>
    );
};
